import { Resources } from '@erecruit/types';
import React, { useRef, useEffect, ReactNode, ReactElement } from 'react';
import moment from 'moment';
import { Modal, H1, Form, Spinner, Text } from '@erecruit/ui-kit';
import { connect } from 'react-redux';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import { Intl } from '@erecruit/core';
import { hasValidationErrors } from '../../AddNewPositionWizard/helpers';
import { PositionField } from '../../AddNewPositionWizard/types';
import {
  ModalConfig,
  PositionSection,
  PositionValues,
  PositionRecordMutation,
  PositionRecordSections,
} from '../types';
import { RootState } from '../../../redux/reducers';
import { renameKeys, convertMomentTimeToString } from '../helpers';
import { populateEditPositionModal, selectors } from '../state/reducer';

import DynamicFormItems from '../../AddNewPositionWizard/components/DynamicFormItems/DynamicFormItems';
import BillingForm from '../../AddNewPositionWizard/components/BillingStep/components/BillingForm';
import { ScheduleStep as ScheduleForm } from '../../AddNewPositionWizard/components/ScheduleStep/ScheduleStep';
import { subtractEndDateFromStartDate } from '../../AddNewPositionWizard/components/ScheduleStep/helpers';

import { LocationStep } from '../../AddNewPositionWizard/components/LocationStep';
import { AdditionalInfoStep } from '../../AddNewPositionWizard/components/AdditionalInfoStep';
import { TIME_PARAM_FORMAT } from '../../../constants';

const { injectIntl } = Intl;

const PageTitle = ({ title }: { title: ReactNode }) => (
  <H1 color="headerTertiary">{title as ReactElement}</H1>
);

const Header = ({
  title,
  description,
}: {
  title: ReactNode;
  description: ReactNode;
}) => (
  <>
    <H1>{title as ReactElement}</H1>
    <Text color="labelPrimary">{description}</Text>
  </>
);

interface EditPositionModalProps extends Intl.InjectedIntlProps {
  visible: boolean;
  isLoading: boolean;
  config: ModalConfig;
  handleToggle: () => void;
  onSavePosition: (positionRecord: PositionRecordMutation) => void;
  populateEditPositionModal?: typeof populateEditPositionModal;
  populatedFields?: PositionField[];
  additionalInfo: Resources.CustomField[];
  sections: PositionRecordSections;
}

export const EditPositionModal = ({
  visible,
  config,
  handleToggle,
  onSavePosition,
  populateEditPositionModal,
  populatedFields,
  isLoading,
  sections,
  intl,
  additionalInfo,
}: EditPositionModalProps) => {
  const formRef = useRef<WrappedFormUtils>();
  const { title, formSpec } = config;
  const { section, description, fields } = formSpec;

  const handleSave = (section: PositionSection) => {
    formRef.current && formRef.current.validateFields();

    if (
      !hasValidationErrors(formRef.current && formRef.current.getFieldsError())
    ) {
      let positionValues = formRef.current && formRef.current.getFieldsValue();

      if (section === 'schedule') {
        positionValues = convertMomentTimeToString<
          PositionValues,
          keyof PositionValues
        >(positionValues as PositionValues, ['startTime', 'endTime']);

        positionValues = {
          ...positionValues,
        };
      }

      const params = renameKeys(positionValues as PositionValues);

      onSavePosition({
        section,
        positionValues: params,
      });
    }
  };

  useEffect(() => {
    if (populateEditPositionModal) {
      populateEditPositionModal(formSpec);
    }
  }, [populateEditPositionModal, formSpec]);

  let formFields = (
    <DynamicFormItems
      section={section}
      fields={populatedFields || fields}
      messageFormatId="vms.positionRecord"
    />
  );

  if (section === 'billing') {
    const durationAmount = subtractEndDateFromStartDate(
      moment(sections.schedule.estimatedStartDate),
      moment(sections.schedule.estimatedEndDate),
    );
    const { numberOfPositions = 1 } = sections.aboutPosition;
    const { startTime, endTime } = sections.schedule;

    if (!formRef.current) {
      formFields = <></>;
    } else {
      formFields = (
        <BillingForm
          form={formRef.current}
          isEdit={true}
          startTime={
            startTime ? moment(startTime, TIME_PARAM_FORMAT) : undefined
          }
          endTime={endTime ? moment(endTime, TIME_PARAM_FORMAT) : undefined}
          numberOfPositions={numberOfPositions}
          durationInDays={durationAmount}
          initialBillingRateData={sections.billing}
        />
      );
    }
  }

  if (section === 'schedule') {
    formFields = <ScheduleForm intl={intl} fields={fields} isEdit={true} />;
  }

  if (section === 'location') {
    formFields = <LocationStep fields={fields} isEdit={true} />;
  }

  if (section === 'additionalInfo') {
    formFields = (
      <AdditionalInfoStep fields={additionalInfo} showHeader={false} />
    );
  }

  return (
    <Modal
      pageTitle={<PageTitle title={title} />}
      title={<Header title={title} description={description} />}
      width={665}
      visible={visible}
      closable={true}
      onCancel={handleToggle}
      onOk={() => handleSave(section)}
    >
      <Form
        onError={alert}
        onValidFieldsSubmit={alert}
        wrappedComponentRef={formRef}
      >
        {isLoading ? <Spinner /> : formFields}
      </Form>
    </Modal>
  );
};

export const EditPositionModalWithIntl = injectIntl(EditPositionModal);

export default connect(
  (state: RootState) => ({
    populatedFields: selectors.selectPositionRecordState(state).populatedFields,
    isLoading: selectors.selectPositionRecordState(state).isLoadingModal,
  }),
  { populateEditPositionModal },
)(EditPositionModalWithIntl);
